import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState, type ReactNode } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { getIntentReport } from "@/lib/analytics/intents.functions";

export const Route = createFileRoute("/_authenticated/admin/sokintention")({
  head: () => ({
    meta: [
      { title: "Sökintention — Fenny admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: IntentPage,
});

const RANGES = [
  { d: 7, label: "7 dagar" },
  { d: 28, label: "28 dagar" },
  { d: 90, label: "90 dagar" },
  { d: 365, label: "12 mån" },
];

const nf = new Intl.NumberFormat("sv-SE");
const dateFmt = new Intl.DateTimeFormat("sv-SE", { dateStyle: "medium" });

function Delta({ now, prev }: { now: number; prev: number }) {
  if (prev === 0 && now === 0) return <span className="text-white/30">—</span>;
  if (prev === 0) return <span className="text-emerald-400">nytt</span>;
  const diff = ((now - prev) / prev) * 100;
  const up = diff >= 0;
  return (
    <span className={up ? "text-emerald-400" : "text-red-400"}>
      {up ? "+" : ""}
      {diff.toFixed(0)}%
    </span>
  );
}

function IntentPage() {
  const [days, setDays] = useState(28);
  const [open, setOpen] = useState<string | null>(null);
  const fn = useServerFn(getIntentReport);

  const q = useQuery({
    queryKey: ["admin", "intents", days],
    queryFn: () => fn({ data: { days } }),
  });

  const report = q.data;


  return (
    <div className="p-4 md:p-8 space-y-6">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Sökintention</h1>
        <p className="text-sm text-white/60 max-w-2xl">
          Varje sida är grupperad efter vad besökaren söker efter. Kolumnen “Besökare” visar hur
          många som klickat sig in, “Hör av sig” hur många av dem som sedan skickat en förfrågan.
        </p>
      </header>

      <div className="flex gap-2">
        {RANGES.map((r) => (
          <button
            key={r.d}
            onClick={() => setDays(r.d)}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              days === r.d ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Hämtar siffror…
        </div>
      )}
      {q.isError && (
        <p className="text-sm text-red-400">Kunde inte hämta rapporten. Ladda om sidan.</p>
      )}

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Kpi
              label="Besökare totalt"
              value={nf.format(report.total_visitors)}
              delta={<Delta now={report.total_visitors} prev={report.prev_total_visitors} />}
            />
            <Kpi
              label="Förfrågningar totalt"
              value={nf.format(report.total_leads)}
              delta={<Delta now={report.total_leads} prev={report.prev_total_leads} />}
            />
            <Kpi
              label="Andel som hör av sig"
              value={
                report.total_visitors > 0
                  ? `${((report.total_leads / report.total_visitors) * 100).toFixed(1)}%`
                  : "—"
              }
            />
          </div>

          <p className="text-xs text-white/40">
            Jämför {days} dagar mot föregående {days} dagar.
            {report.measuring_since
              ? ` Mätningen startade ${dateFmt.format(new Date(report.measuring_since))} — perioder före det saknar data.`
              : " Mätningen har inte samlat in någon data ännu."}
          </p>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-white/60">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Sökintention</th>
                  <th className="px-4 py-3 text-right font-medium">Besökare</th>
                  <th className="px-4 py-3 text-right font-medium">Förr</th>
                  <th className="px-4 py-3 text-right font-medium hidden md:table-cell">Från sök</th>
                  <th className="px-4 py-3 text-right font-medium hidden md:table-cell">Sidvisningar</th>
                  <th className="px-4 py-3 text-right font-medium">Hör av sig</th>
                  <th className="px-4 py-3 text-right font-medium">Förr</th>
                  <th className="px-4 py-3 text-right font-medium">Andel</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row) => {
                  const isOpen = open === row.id;
                  return (
                    <Fragment key={row.id}>
                      <tr
                        onClick={() => setOpen(isOpen ? null : row.id)}
                        className="border-t border-white/10 cursor-pointer hover:bg-white/[0.03]"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium">{row.label}</div>
                          <div className="text-xs text-white/50">{row.description}</div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{nf.format(row.visitors)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-xs">
                          <span className="text-white/40">{nf.format(row.prev_visitors)}</span>{" "}
                          <Delta now={row.visitors} prev={row.prev_visitors} />
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-white/70">
                          {nf.format(row.search_visitors)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-white/70">
                          {nf.format(row.pageviews)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{nf.format(row.leads)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-xs">
                          <span className="text-white/40">{nf.format(row.prev_leads)}</span>{" "}
                          <Delta now={row.leads} prev={row.prev_leads} />
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {row.conversion_pct == null ? "—" : `${row.conversion_pct.toFixed(1)}%`}
                        </td>

                        <td className="px-2 py-3 text-right text-white/40">
                          <ChevronDown className={`h-4 w-4 inline transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-white/10 bg-white/[0.02]">
                          <td colSpan={7} className="px-4 py-3">
                            {row.top_pages.length === 0 ? (
                              <p className="text-xs text-white/50">Inga besök i perioden.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead className="text-white/50">
                                  <tr>
                                    <th className="py-1 text-left font-medium">Sida</th>
                                    <th className="py-1 text-right font-medium">Besökare</th>
                                    <th className="py-1 text-right font-medium">Sidvisningar</th>
                                    <th className="py-1 text-right font-medium">Förfrågningar härifrån</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.top_pages.map((p) => (
                                    <tr key={p.path} className="border-t border-white/5">
                                      <td className="py-1.5 pr-3 text-white/80">{p.path}</td>
                                      <td className="py-1.5 text-right tabular-nums">{nf.format(p.visitors)}</td>
                                      <td className="py-1.5 text-right tabular-nums text-white/60">{nf.format(p.pageviews)}</td>
                                      <td className="py-1.5 text-right tabular-nums">{nf.format(p.leads)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            <p className="mt-3 text-xs text-white/40">
                              Förfrågningar härifrån = formuläret skickades på just den sidan.
                              “Hör av sig” räknar även besökare som läste sidan först och hörde av
                              sig från en annan sida.
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, delta }: { label: string; value: string; delta?: ReactNode }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-xs text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {delta && <div className="mt-1 text-xs">{delta}</div>}
    </div>
  );
}
